import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, ANNOUNCEMENT_LABEL, uploadFile } from "../../lib/api";
import { exportPollPdf } from "../../lib/pdf";
import { Spinner, EmptyState, Button, Field, Chip, Sheet } from "../../components/ui";
import { Icon } from "../../components/icons";

const TYPES = Object.keys(ANNOUNCEMENT_LABEL);
let seq = 0;
const newId = () => `opt-${(seq += 1)}`;
const who = (m) => `${m.name}${m.voice ? ` (${m.voice[0]})` : ""}`;
const fmt = (iso) => new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

// Full payload the shared backend expects on PUT (same as the Mobile app).
const payloadOf = (a, patch = {}) => ({
  type: a.type, title: a.title, body: a.body || "", video_url: a.video_url || "",
  attachments: (a.attachments || []).map(({ file_id, kind, filename, content_type }) => ({ file_id, kind, filename, content_type })),
  poll: a.poll ? { question: a.poll.question || "", options: a.poll.options, multiple: !!a.poll.multiple, closes_at: a.poll.closes_at || null } : null,
  require_ack: !!a.require_ack, pinned: !!a.pinned, active: a.active !== false, ...patch,
});

/** Plain-text poll summary (to paste in WhatsApp/email). */
export function pollSummaryText(a, d) {
  const lines = [`Sondagem · ${a.title}`, a.poll.question || "", `${d.voted}/${d.total} responderam`, ""];
  a.poll.options.forEach((o, i) => {
    const v = d.members.filter((m) => m.vote?.includes(i));
    const pct = d.voted ? Math.round((v.length / d.voted) * 100) : 0;
    lines.push(`• ${o}: ${v.length} (${pct}%)${v.length ? ` — ${v.map(who).join(", ")}` : ""}`);
  });
  const none = d.members.filter((m) => m.vote === null);
  if (none.length) lines.push("", `Sem resposta (${none.length}): ${none.map(who).join(", ")}`);
  return lines.filter((l, i) => l || i > 1).join("\n");
}

function Stats({ a }) {
  const q = useQuery({ queryKey: ["announcement-stats", a.id], queryFn: () => api(`/admin/announcements/${a.id}/stats`) });
  const [copied, setCopied] = useState(false);
  if (q.isLoading) return <Spinner />;
  if (!q.data) return null;
  const d = q.data;
  const read = d.members.filter((m) => m.read_at);
  const pending = d.members.filter((m) => !m.read_at);
  const none = a.poll ? d.members.filter((m) => m.vote === null) : [];
  const copy = async () => { await navigator.clipboard.writeText(pollSummaryText(a, d)); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, borderTop: "1px solid var(--divider)", paddingTop: 12 }} data-testid={`announcement-stats-${a.id}`}>
      <div className="t-kicker">QUEM VIU · {read.length}/{d.total} CONFIRMARAM LEITURA</div>
      {a.require_ack ? null : <div className="t-hint">Este alerta não pede confirmação de leitura: só aparece aqui quem a confirmou. Ative «Pedir confirmação de leitura» para acompanhar todos.</div>}
      {read.length ? <div style={{ fontSize: 13 }} data-testid={`announcement-read-${a.id}`}>{read.map((m) => `${who(m)} · ${fmt(m.read_at)}`).join("; ")}</div> : null}
      {pending.length ? <div className="t-hint" data-testid={`announcement-pending-${a.id}`}>Por ler ({pending.length}): {pending.map(who).join(", ")}</div> : null}
      {a.poll ? (
        <>
          <div className="t-kicker" style={{ marginTop: 6 }}>SONDAGEM · {d.voted}/{d.total} RESPONDERAM</div>
          {a.poll.options.map((o, i) => {
            const v = d.members.filter((m) => m.vote?.includes(i));
            return <div key={`${i}-${o}`} style={{ fontSize: 13 }} data-testid={`announcement-result-${a.id}-${i}`}><b>{o}</b> · {v.length}{v.length ? `: ${v.map(who).join(", ")}` : ""}</div>;
          })}
          {none.length ? <div className="t-hint">Sem resposta ({none.length}): {none.map(who).join(", ")}</div> : null}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button className="auto sm ghost" icon="download" onClick={() => exportPollPdf(a, d)} data-testid={`announcement-summary-pdf-${a.id}`}>RESUMO PDF</Button>
            <Button className="auto sm ghost" icon="copy" onClick={copy} data-testid={`announcement-summary-copy-${a.id}`}>{copied ? "COPIADO" : "COPIAR RESUMO"}</Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Toggle({ label, hint, value, onChange, testid }) {
  return (
    <label className="card" style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, cursor: "pointer" }} data-testid={testid}>
      <span style={{ flex: 1 }}><span style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{label}</span>{hint ? <span className="t-hint" style={{ display: "block", fontSize: 12 }}>{hint}</span> : null}</span>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: "var(--brand-primary)", width: 18, height: 18 }} data-testid={`${testid}-input`} />
    </label>
  );
}

function AnnouncementForm({ initial, onClose, onSaved }) {
  const [type, setType] = useState(initial?.type || "comunicado");
  const [title, setTitle] = useState(initial?.title || "");
  const [body, setBody] = useState(initial?.body || "");
  const [video, setVideo] = useState(initial?.video_url || "");
  const [attachments, setAttachments] = useState(initial?.attachments || []);
  const [question, setQuestion] = useState(initial?.poll?.question || "");
  // Each editable option keeps a stable id so removing one never shifts the inputs below it.
  const [options, setOptions] = useState(() => (initial?.poll?.options?.length ? initial.poll.options : ["Sim", "Não"]).map((text) => ({ id: newId(), text })));
  const [multiple, setMultiple] = useState(!!initial?.poll?.multiple);
  const [requireAck, setRequireAck] = useState(initial ? !!initial.require_ack : true);
  const [pinned, setPinned] = useState(!!initial?.pinned);
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState("");
  const editingPoll = !!initial?.poll?.voters;

  const upload = async (e, kind) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    for (const f of files) {
      setProgress(0);
      try {
        const up = await uploadFile("/admin/announcements/upload", f, setProgress);
        setAttachments((cur) => [...cur, { file_id: up.file_id || up.id, kind: up.kind || (kind === "image" || (up.content_type || f.type).startsWith("image/") ? "image" : "file"), filename: up.filename || f.name, content_type: up.content_type || f.type, url: up.url }]);
      } catch (ex) { setErr(ex.message); }
    }
    setProgress(null);
  };
  const save = useMutation({
    mutationFn: () => {
      if (!title.trim()) throw new Error("Indique um título.");
      const opts = options.map((o) => o.text.trim()).filter(Boolean);
      if (type === "sondagem" && opts.length < 2) throw new Error("A sondagem precisa de pelo menos 2 opções.");
      const payload = payloadOf({ ...(initial || {}), type, title: title.trim(), body: body.trim(), video_url: video.trim(), attachments, require_ack: requireAck, pinned, poll: type === "sondagem" ? { question: question.trim(), options: opts, multiple, closes_at: initial?.poll?.closes_at || null } : null });
      return api(initial?.id ? `/admin/announcements/${initial.id}` : "/admin/announcements", { method: initial?.id ? "PUT" : "POST", body: JSON.stringify(payload) });
    },
    onSuccess: onSaved,
    onError: (e) => setErr(e.message),
  });

  return (
    <Sheet title={initial?.id ? "Editar alerta" : "Novo alerta"} onClose={onClose} testid="announcement-form" wide>
      <div className="t-section">Tipo</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {TYPES.map((t) => <Chip key={t} label={ANNOUNCEMENT_LABEL[t].toUpperCase()} active={type === t} onClick={() => setType(t)} data-testid={`announcement-type-${t}`} />)}
      </div>
      <Field label="TÍTULO" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex.: Ensaio extra sábado às 10h" data-testid="announcement-title-input" />
      <Field as="textarea" label="MENSAGEM" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Texto do comunicado…" data-testid="announcement-body-input" />
      {type === "sondagem" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="announcement-poll-editor">
          <div className="t-section">Sondagem</div>
          <Field label="PERGUNTA" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ex.: Está disponível para o concerto de 20 de junho?" data-testid="announcement-question-input" />
          {editingPoll ? <div className="t-hint">Já há respostas: alterar ou remover opções pode baralhar os resultados.</div> : null}
          {options.map((o, i) => (
            <div key={o.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input className="input" value={o.text} onChange={(e) => setOptions((cur) => cur.map((x) => (x.id === o.id ? { ...x, text: e.target.value } : x)))} placeholder={`Opção ${i + 1}`} style={{ flex: 1, minWidth: 0 }} data-testid={`announcement-option-${i}`} />
              <button className="icon-btn" disabled={options.length <= 2} onClick={() => setOptions((cur) => cur.filter((x) => x.id !== o.id))} aria-label={`Remover opção ${i + 1}`} data-testid={`announcement-option-remove-${i}`}><Icon name="close" size={15} /></button>
            </div>
          ))}
          {options.length < 10 ? <Button className="auto sm ghost" icon="add" onClick={() => setOptions((cur) => [...cur, { id: newId(), text: "" }])} data-testid="announcement-option-add">ADICIONAR OPÇÃO</Button> : null}
          <Toggle label="Permitir escolher várias opções" value={multiple} onChange={setMultiple} testid="announcement-multiple" />
        </div>
      ) : null}
      <div className="t-section">Fotos, ficheiros e vídeo</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <label className="chip" style={{ cursor: "pointer" }} data-testid="announcement-add-photo"><Icon name="image" size={14} /> FOTO<input type="file" accept="image/*" multiple hidden onChange={(e) => upload(e, "image")} /></label>
        <label className="chip" style={{ cursor: "pointer" }} data-testid="announcement-add-file"><Icon name="attach" size={14} /> FICHEIRO<input type="file" multiple hidden onChange={(e) => upload(e, "file")} /></label>
        {progress !== null ? <span className="t-hint">A carregar… {Math.round(progress * 100)}%</span> : null}
      </div>
      {attachments.length ? (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {attachments.map((x) => <span key={x.file_id} className="chip" data-testid={`announcement-attachment-${x.file_id}`}><Icon name={x.kind === "image" ? "image" : "document-text"} size={13} /> {x.filename}<button className="icon-btn" style={{ width: 22, height: 22 }} onClick={() => setAttachments((cur) => cur.filter((y) => y.file_id !== x.file_id))} aria-label={`Remover ${x.filename}`}><Icon name="close" size={12} /></button></span>)}
        </div>
      ) : null}
      <Field label="LINK DE VÍDEO (YOUTUBE, VIMEO, …)" value={video} onChange={(e) => setVideo(e.target.value)} placeholder="https://…" data-testid="announcement-video-input" />
      <div className="t-section">Opções</div>
      <Toggle label="Pedir confirmação de leitura" hint="Cada membro vê o botão «Confirmar leitura»; acompanha quem já leu." value={requireAck} onChange={setRequireAck} testid="announcement-require-ack" />
      <Toggle label="Fixar no topo do Início" value={pinned} onChange={setPinned} testid="announcement-pinned" />
      {err ? <div style={{ background: "var(--error)", color: "var(--on-error)", padding: 10, borderRadius: "var(--r-md)", fontSize: 13 }} data-testid="announcement-form-error">{err}</div> : null}
      <Button loading={save.isPending} disabled={progress !== null} onClick={() => { setErr(""); save.mutate(); }} data-testid="announcement-save">{initial?.id ? "GUARDAR ALTERAÇÕES" : "PUBLICAR ALERTA"}</Button>
    </Sheet>
  );
}

export default function AdminAnnouncements() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(null);
  const q = useQuery({ queryKey: ["announcements", "all"], queryFn: () => api("/announcements?all=1") });
  const refresh = () => qc.invalidateQueries({ queryKey: ["announcements"] });
  const toggleActive = useMutation({ mutationFn: (a) => api(`/admin/announcements/${a.id}`, { method: "PUT", body: JSON.stringify(payloadOf(a, { active: a.active === false })) }), onSuccess: refresh, onError: (e) => window.alert(e.message) });
  const remove = useMutation({ mutationFn: (id) => api(`/admin/announcements/${id}`, { method: "DELETE" }), onSuccess: refresh, onError: (e) => window.alert(e.message) });
  const list = q.data || [];
  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" aria-label="Voltar" onClick={() => navigate(-1)}><Icon name="chevron-back" size={22} /></button>
        <h1 className="t-h1" style={{ flex: 1 }}>Alertas</h1>
        <Button className="auto sm" icon="add" onClick={() => setEditing({})} data-testid="add-announcement-button">NOVO</Button>
      </div>
      {q.isLoading ? <Spinner /> : (
        <div className="page ulc-fade" style={{ maxWidth: 760 }}>
          {!list.length ? <EmptyState icon="megaphone" text="Ainda não criou alertas. Crie um comunicado, alerta ou sondagem." testid="admin-announcements-empty" /> : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {list.map((a) => (
              <div key={a.id} className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, opacity: a.active === false ? 0.6 : 1 }} data-testid={`admin-announcement-${a.id}`}>
                <button onClick={() => setOpen(open === a.id ? null : a.id)} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "none", border: "none", color: "inherit", padding: 0, textAlign: "left", cursor: "pointer" }} data-testid={`admin-announcement-toggle-${a.id}`}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-kicker">{(ANNOUNCEMENT_LABEL[a.type] || "Aviso").toUpperCase()}{a.pinned ? " · FIXADO" : ""}{a.active === false ? " · OCULTO" : ""} · {fmt(a.created_at)}</div>
                    <div style={{ fontFamily: "var(--f-medium)", fontSize: 16, marginTop: 4 }}>{a.title}</div>
                    <div className="t-hint" style={{ marginTop: 2 }}>{a.require_ack ? `${a.ack_count || 0} confirmaram leitura` : "Sem confirmação de leitura"}{a.poll ? ` · ${a.poll.voters || 0} respostas` : ""}</div>
                  </div>
                  <Icon name={open === a.id ? "chevron-up" : "chevron-down"} size={16} color="var(--muted)" />
                </button>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Button className="auto sm ghost" icon="pencil" onClick={() => setEditing(a)} data-testid={`announcement-edit-${a.id}`}>EDITAR</Button>
                  <Button className="auto sm ghost" icon={a.active === false ? "eye" : "eye-off"} onClick={() => toggleActive.mutate(a)} data-testid={`announcement-active-${a.id}`}>{a.active === false ? "MOSTRAR" : "OCULTAR"}</Button>
                  <Button className="auto sm ghost" icon="trash" onClick={() => window.confirm(`Apagar «${a.title}»? As confirmações/votos serão eliminados para todos.`) && remove.mutate(a.id)} data-testid={`announcement-delete-${a.id}`}>APAGAR</Button>
                </div>
                {open === a.id ? <Stats a={a} /> : null}
              </div>
            ))}
          </div>
        </div>
      )}
      {editing ? <AnnouncementForm initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { refresh(); setEditing(null); }} /> : null}
    </>
  );
}
